import React, { useState } from 'react';
import { Search, Filter, Star, MapPin, Calendar, DollarSign, Users, Clock, Eye, X, Bookmark, ExternalLink, Send, FileText, User, Edit, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { toast } from '../../hooks/use-toast';
import TrainingProposalForm from '../forms/TrainingProposalForm';
import ProposalUpload from '../forms/ProposalUpload';

 
const TrainingOpportunities = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [savedOpportunities, setSavedOpportunities] = useState([]);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [showApplyOptions, setShowApplyOptions] = useState(false);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [applyingOpportunity, setApplyingOpportunity] = useState(null);

  const opportunities = [
    {
      id: 1,
      title: 'Executive Leadership Development Program',
      company: 'Fortune 500 Tech Company',
      location: 'San Francisco, CA',
      budget: '$15,000 - $25,000',
      duration: '3-day intensive workshop',
      participants: '20-25 executives',
      startDate: '2024-04-15',
      status: 'Active',
      urgency: 'High',
      description: 'Looking for an experienced leadership trainer to deliver a comprehensive program for senior executives focusing on digital transformation leadership.',
      requirements: ['10+ years experience', 'Executive coaching background', 'Digital transformation expertise'],
      posted: '2 days ago'
    },
    {
      id: 2,
      title: 'Remote Team Management Workshop',
      company: 'Global Consulting Firm',
      location: 'Virtual/Remote',
      budget: '$8,000 - $12,000',
      duration: '2-day workshop',
      participants: '50-60 managers',
      startDate: '2024-03-28',
      status: 'Active',
      urgency: 'Medium',
      description: 'Seeking trainer for virtual workshop on managing distributed teams and remote work best practices.',
      requirements: ['Remote training experience', 'Change management skills', 'Virtual facilitation tools'],
      posted: '5 days ago'
    },
    {
      id: 3,
      title: 'Diversity & Inclusion Training Series',
      company: 'Financial Services Corp',
      location: 'New York, NY',
      budget: '$20,000 - $35,000',
      duration: '4-session series',
      participants: '100+ employees',
      startDate: '2024-05-01',
      status: 'Active',
      urgency: 'Medium',
      description: 'Multi-session training program to enhance diversity, equity, and inclusion across the organization.',
      requirements: ['D&I certification', 'Corporate experience', 'Interactive training methods'],
      posted: '1 week ago'
    },
    {
      id: 4,
      title: 'Sales Team Coaching Program',
      company: 'SaaS Startup',
      location: 'Austin, TX',
      budget: '$5,000 - $8,000',
      duration: '1-day workshop + follow-up',
      participants: '15-20 sales reps',
      startDate: '2024-04-05',
      status: 'New',
      urgency: 'High',
      description: 'Intensive sales coaching focused on consultative selling and customer relationship building.',
      requirements: ['Sales training background', 'SaaS industry knowledge', 'Coaching certification'],
      posted: '3 hours ago'
    }
  ];

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'New': return 'bg-blue-100 text-blue-800';
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Applied': return 'bg-purple-100 text-purple-800';
      case 'Closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Handler functions
  const handleApplyNow = (opportunity) => {
    setApplyingOpportunity(opportunity);
    setShowApplyOptions(true);
  };

  const handleFormFlowClick = () => {
    setShowApplyOptions(false);
    setShowProposalForm(true);
  };

  const handleUploadClick = () => {
    setShowApplyOptions(false);
    setShowUploadForm(true);
  };

  const handleProposalSubmit = (formData, files) => {
    console.log('Form proposal submitted:', formData, files);
    toast({
      title: "Proposal Submitted Successfully!",
      description: "Your detailed proposal has been sent to the client. You'll receive updates via email.",
    });
    setShowProposalForm(false);
    setApplyingOpportunity(null);
  };

  const handleUploadSubmit = (submissionData) => {
    console.log('Upload proposal submitted:', submissionData);
    toast({
      title: "Proposal Uploaded Successfully!",
      description: "Your proposal documents have been submitted. You'll receive updates via email.",
    });
    setShowUploadForm(false);
    setApplyingOpportunity(null);
  };

  const handleSaveForLater = (opportunityId) => {
    if (savedOpportunities.includes(opportunityId)) {
      setSavedOpportunities(savedOpportunities.filter(id => id !== opportunityId));
      toast({
        title: "Opportunity Removed",
        description: "Training opportunity removed from your saved list.",
      });
    } else {
      setSavedOpportunities([...savedOpportunities, opportunityId]);
      toast({
        title: "Opportunity Saved!",
        description: "Training opportunity added to your saved list.",
      });
    }
  };

  const handleViewDetails = (opportunity) => {
    setSelectedOpportunity(opportunity);
  };

  const closeDetails = () => {
    setSelectedOpportunity(null);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Training Opportunities ({opportunities.length})</span>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search opportunities..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64"
                />
              </div>
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {opportunities.map((opportunity) => (
              <Card key={opportunity.id} className="border-2 hover:border-green-200 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-xl font-semibold text-gray-900 hover:text-green-600 cursor-pointer">
                          {opportunity.title}
                        </h3>
                        <div className="flex items-center space-x-2">
                          <Badge className={getStatusColor(opportunity.status)}>
                            {opportunity.status}
                          </Badge>
                          <Badge className={getUrgencyColor(opportunity.urgency)}>
                            {opportunity.urgency} Priority
                          </Badge>
                        </div>
                      </div>
                      
                      <p className="text-blue-600 font-medium mb-1">{opportunity.company}</p>
                      <p className="text-gray-600 mb-3">{opportunity.description}</p>
                      
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="w-4 h-4 mr-2" />
                            {opportunity.location}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <DollarSign className="w-4 h-4 mr-2" />
                            {opportunity.budget}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="w-4 h-4 mr-2" />
                            {opportunity.duration}
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="w-4 h-4 mr-2" />
                            {opportunity.participants}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            Starts: {opportunity.startDate}
                          </div>
                          <div className="text-xs text-gray-500">
                            Posted: {opportunity.posted}
                          </div>
                        </div>
                      </div>
                      
                      <div className="mb-4">
                        <p className="text-sm font-medium text-gray-700 mb-2">Requirements:</p>
                        <div className="flex flex-wrap gap-2">
                          {opportunity.requirements.map((req, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {req}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApplyNow(opportunity)}
                          >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            Apply Now
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleSaveForLater(opportunity.id)}
                          >
                            <Bookmark className={`w-4 h-4 mr-1 ${savedOpportunities.includes(opportunity.id) ? 'fill-current' : ''}`} />
                            {savedOpportunities.includes(opportunity.id) ? 'Saved' : 'Save for Later'}
                          </Button>
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => handleViewDetails(opportunity)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Details
                          </Button>
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <Star className="w-4 h-4 mr-1" />
                          Match Score: 85%
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Opportunity Details Modal */}
      {selectedOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
              <h2 className="text-2xl font-bold">{selectedOpportunity.title}</h2>
              <Button variant="ghost" size="sm" onClick={closeDetails}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Company Info */}
              <div className="flex items-start space-x-4">
                <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {selectedOpportunity.company[0]}
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-xl">{selectedOpportunity.company}</h3>
                  <div className="flex items-center space-x-4 text-gray-600 mt-1">
                    <div className="flex items-center">
                      <MapPin className="w-4 h-4 mr-1" />
                      {selectedOpportunity.location}
                    </div>
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      {selectedOpportunity.startDate}
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      {selectedOpportunity.duration}
                    </div>
                  </div>
                </div>
              </div>

              {/* Opportunity Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <h4 className="font-semibold mb-2">Budget Range</h4>
                  <p className="text-gray-700">{selectedOpportunity.budget}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Participants</h4>
                  <p className="text-gray-700">{selectedOpportunity.participants}</p>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">Urgency</h4>
                  <Badge className={getUrgencyColor(selectedOpportunity.urgency)}>
                    {selectedOpportunity.urgency}
                  </Badge>
                </div>
              </div>

              {/* Description */}
              <div>
                <h4 className="font-semibold mb-3">Opportunity Description</h4>
                <p className="text-gray-700 leading-relaxed">
                  {selectedOpportunity.description}
                </p>
              </div>

              {/* Requirements */}
              {selectedOpportunity.requirements && (
                <div>
                  <h4 className="font-semibold mb-3">Requirements</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedOpportunity.requirements.map((requirement, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
                        <span className="text-gray-700">{requirement}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex space-x-4 pt-4 border-t">
                <Button 
                  onClick={() => handleApplyNow(selectedOpportunity)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Apply Now
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => handleSaveForLater(selectedOpportunity.id)}
                  className="flex items-center"
                >
                  <Bookmark className={`w-4 h-4 mr-2 ${savedOpportunities.includes(selectedOpportunity.id) ? 'fill-current' : ''}`} />
                  {savedOpportunities.includes(selectedOpportunity.id) ? 'Saved' : 'Save for Later'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

 

      {/* Apply Options Modal */}
      {showApplyOptions && applyingOpportunity && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full">
            <div className="p-6 border-b flex justify-between items-center">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Choose Application Method</h3>
                <p className="text-gray-600 mt-1">For: {applyingOpportunity.title}</p>
              </div>
              <button 
                onClick={() => setShowApplyOptions(false)}
                className="p-2 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Detailed Form Option */}
                <div 
                  onClick={handleFormFlowClick}
                  className="border-2 border-gray-200 rounded-lg p-6 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-all"
                >
                  <div className="text-center">
                    <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                      <Edit className="w-8 h-8 text-blue-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Create Detailed Proposal</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Step-by-step guided form to create a comprehensive training proposal
                    </p>
                    <ul className="text-xs text-gray-500 text-left space-y-1">
                      <li>• Guided 7-step process</li>
                      <li>• Built-in templates and examples</li>
                      <li>• Professional formatting</li>
                      <li>• Field validation and tips</li>
                    </ul>
                    <div className="mt-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        Recommended
                      </span>
                    </div>
                  </div>
                </div>

                {/* Upload Option */}
                <div 
                  onClick={handleUploadClick}
                  className="border-2 border-gray-200 rounded-lg p-6 cursor-pointer hover:border-green-400 hover:bg-green-50 transition-all"
                >
                  <div className="text-center">
                    <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <Upload className="w-8 h-8 text-green-600" />
                    </div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload Existing Proposal</h4>
                    <p className="text-sm text-gray-600 mb-4">
                      Upload your pre-prepared proposal documents and supporting materials
                    </p>
                    <ul className="text-xs text-gray-500 text-left space-y-1">
                      <li>• Upload multiple documents</li>
                      <li>• Support for PDF, DOC, PPT</li>
                      <li>• Quick submission process</li>
                      <li>• Attach portfolio materials</li>
                    </ul>
                    <div className="mt-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Fast Track
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600 text-center">
                  <strong>Need help deciding?</strong> Use the detailed form if you're new to proposal writing or want guidance. 
                  Use the upload option if you already have a complete proposal ready.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Training Proposal Form Modal */}
      {showProposalForm && (
        <TrainingProposalForm
          opportunity={applyingOpportunity}
          onClose={() => setShowProposalForm(false)}
          onSubmit={handleProposalSubmit}
        />
      )}

      {/* Upload Proposal Modal */}
      {showUploadForm && (
        <ProposalUpload
          opportunity={applyingOpportunity}
          onClose={() => setShowUploadForm(false)}
          onSubmit={handleUploadSubmit}
        />
      )}
    </div>
  );
};

export default TrainingOpportunities;